import {
    useCalculateKostnadskalkyleQuery,
    useGetKostnadskjemaAverageQuery,
} from '@klp/prosjekt/services/rtkApi';
import { useCalculateKalkylePayload } from './useCalculateSummaryPayload';
import { useFormikContext } from 'formik';
import type {
    KostnadSkjemaAverageCost,
    KostnadSkjemaNokkeltall,
    LonnsomhetNokkeltall,
    KostnadskjemaUserInput,
    DelsumInput,
} from '@klp/prosjekt/services/types';
import { useCallback, useMemo } from 'react';

export const useNokkeltallSammendrag = () => {
    const payload = useCalculateKalkylePayload();
    const { values, setFieldValue } =
        useFormikContext<KostnadskjemaUserInput>();

    const shouldSkipCalculation =
        !payload.kostnadslinjer.length ||
        payload.kostnadslinjer.every(
            (line) =>
                !line.kostnadstype?.id ||
                (line.prisPrEnhet === 0 && line.antallEnhet === 0),
        );

    const { data: calculateData, isFetching: isCalcFetching } =
        useCalculateKostnadskalkyleQuery(payload, {
            skip: shouldSkipCalculation,
        });
    const { data: avgCost, isLoading: isAvgLoading } =
        useGetKostnadskjemaAverageQuery({
            prosessInstansId: payload.prosessInstansId,
        });

    const addDelsum = useCallback(() => {
        const newDelsum: DelsumInput = {
            navn: `Delprosjekt ${values.delsummer.length + 1}`,
            avskrivningstid: 0,
            restverdiIProsent: 0,
        };
        setFieldValue('delsummer', [...values.delsummer, newDelsum]);
    }, [values.delsummer, setFieldValue]);

    // Don't return data until we have a valid calculation
    const nokkeltall = useMemo(() => {
        if (shouldSkipCalculation || !calculateData) {
            return undefined;
        }

        const result: LonnsomhetNokkeltall &
            KostnadSkjemaNokkeltall &
            KostnadSkjemaAverageCost = {
            ...calculateData,
            gjennomsnittligKostnadPerM2: avgCost ?? 0,
        };

        return result;
    }, [calculateData, avgCost, shouldSkipCalculation]);

    return {
        nokkeltall, // Contains calculated delsummer with kostnad (read-only)
        addDelsum,
        nokkeltallIsLoading: isCalcFetching || isAvgLoading,
    };
};
